import { createContext, useContext, useState, useEffect } from 'react'
import { auth } from '../services/firebase'
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut
} from 'firebase/auth'
import { getUserProfile, createUserProfile } from '../services/userService'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [userProfile, setUserProfile] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                setUser(firebaseUser)
                try {
                    const profile = await getUserProfile(firebaseUser.uid)
                    setUserProfile(profile)
                } catch (err) {
                    console.error('Error fetching user profile:', err)
                }
            } else {
                setUser(null)
                setUserProfile(null)
            }
            setLoading(false)
        })

        return () => unsubscribe()
    }, [])

    const login = async (email, password) => {
        setError(null)
        try {
            const result = await signInWithEmailAndPassword(auth, email, password)
            return result.user
        } catch (err) {
            setError(getAuthErrorMessage(err.code))
            throw err
        }
    }

    const register = async (email, password, retentionPolicyAccepted) => {
        setError(null)
        try {
            const result = await createUserWithEmailAndPassword(auth, email, password)

            // Create user profile in Firestore
            await createUserProfile(result.user.uid, {
                email,
                retentionPolicyAccepted,
                role: 'paid', // Temporary paid access
                saveHistory: false, // History tracking disabled by default
                trialEndsAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
                createdAt: new Date(),
                currency: 'USD'
            })

            return result.user
        } catch (err) {
            setError(getAuthErrorMessage(err.code))
            throw err
        }
    }

    const logout = async () => {
        try {
            await signOut(auth)
        } catch (err) {
            setError('Failed to log out')
            throw err
        }
    }

    const value = {
        user,
        userProfile,
        loading,
        error,
        login,
        register,
        logout,
        setError
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

// Helper function to convert Firebase error codes to user-friendly messages
function getAuthErrorMessage(code) {
    switch (code) {
        case 'auth/email-already-in-use':
            return 'This email is already registered. Please sign in instead.'
        case 'auth/invalid-email':
            return 'Please enter a valid email address.'
        case 'auth/operation-not-allowed':
            return 'Email/password accounts are not enabled. Please contact support.'
        case 'auth/weak-password':
            return 'Password is too weak. Please use at least 6 characters.'
        case 'auth/user-disabled':
            return 'This account has been disabled. Please contact support.'
        case 'auth/user-not-found':
            return 'No account found with this email.'
        case 'auth/wrong-password':
            return 'Incorrect password. Please try again.'
        case 'auth/too-many-requests':
            return 'Too many failed attempts. Please try again later.'
        default:
            return 'An error occurred. Please try again.'
    }
}
